import { useEffect, useLayoutEffect, useRef } from 'react'

import { isAvatarSrc, thumbnailMediumBlue } from '@utils/thumbnails'
import { useNavigationType } from 'react-router-dom'
import useModalContext from '@contexts/modalContext'
import { fontSize, size } from '@styles/theme'
import styled from '@emotion/styled/macro'

import ProfileNavigateAvatar from '@elements/ProfileNavigateAvatar'
import { SubscriberIcon } from '@elements/icons/SubscriberIcon'
import { PiNewspaperBold } from 'react-icons/pi'
import Post from '@components/feeds/post/Post'

const NoPostsDisplay = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: ${size(10)} auto ${size(10)} auto;

  font-size: ${size(2.75)};
  color: rgba(255,255,255,0.15);
`

const SocialInfo = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${size(2)};
  margin-top: ${size(6)};
`

const SocialInfoLeft = styled.div`
  display: flex;
  align-items: center;
`

const SocialInfoDisplayNames = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${size(1)};
`

const DisplayName = styled.b`
  display: flex;
  align-items: center;
  font-size: ${fontSize('xl')};
  letter-spacing: 0.35px;
  margin-bottom: ${size(0.5)};
  color: white;
`

const Handle = styled.b`
  font-size: ${fontSize('sm')};
  letter-spacing: 0.35px;
  color: grey;
`

const RecentPosts = styled.h4`
  padding-left: ${size(3)};
`

const profilePictureStyles = (isAvatar) => ({
  filter: isAvatar && 'grayscale(100%) contrast(120%)' ,
  height: size(7),
  width: size(7),
})

const ProfileRecentPosts = ({ user, userHasStats, feedState={} }) => {
  const {
    posts,
    setPosts,
    setPostsLoaded,
    getInitialPosts,
    setLikeToState,
    setUnlikeToState,
    setRepostToState,
    newReplyAfterCreate,
    afterPostDelete,
    feedAccessor,
  } = feedState
  const { nearbyProfile } = useModalContext()
  const navigationType = useNavigationType()
  const refetchPostsCountRef = useRef(0)
  const isAvatar = isAvatarSrc('medium', user?.socialMedia?.thumbnail, user?.defaultAvatar)
  const hasPosts = (posts?.length && posts?.length > 0)

  // Resets user posts when a new modal is opened
  useLayoutEffect(() => {
    if (nearbyProfile.isOpen && navigationType === 'PUSH') {
      setPosts([])
    }
  }, [nearbyProfile?.isOpen])

  // Retrieves user posts on modal open (after their removal above)
  useEffect(() => {
    if (user?.uuid && nearbyProfile?.isOpen) {
      // NOTE: This is necessary for the feed to scroll correctly back to its original backnavigated location.
      const oldPosts = getPostsFromStorage()
      // Use the old post data if the old posts exists, then attempt to get new post data if not back navigating
      if (oldPosts?.length > 0) {
        setPosts(oldPosts)
        setPostsLoaded(true)
      }

      if (navigationType !== 'POP') {
        getInitialPosts()
      }
    } else {
      setPostsLoaded(false)
      refetchPostsCountRef.current = 0
    }
  }, [user?.uuid, nearbyProfile?.isOpen])

  // Re-Retrieves posts when the user shown is the current user, and the thumbnails have changed.
  // This may need to be updated to include the display name
  useLayoutEffect(() => {
    if (
      // NOTE: The refetch count is just a (admittedly bad) way to prevent infinite loops
      //       in cases where the below conditions may render always true (even though they shouldn't)
      refetchPostsCountRef.current < 20 &&
      posts?.length > 0 &&
      posts[0].user &&
      user &&
      posts[0].user.uuid === user.uuid &&
      posts[0].user?.socialMedia?.thumbnail?.uuid !== user?.socialMedia?.thumbnail?.uuid
    ) {
      refetchPostsCountRef.current++
      getInitialPosts()
    }
  }, [posts, user?.socialMedia?.thumbnail?.uuid])
  
  const getPostsFromStorage = () => {
    if (!user?.uuid) return;
    return JSON.parse(sessionStorage.getItem(feedAccessor))
  }

  const renderPosts = () => posts.map((post, index) => (
    <Post
      post={post}
      beforeLikeHook={setLikeToState}
      beforeUnlikeHook={setUnlikeToState}
      beforeRepostHook={setRepostToState}
      newReplyAfterCreate={newReplyAfterCreate}
      afterDelete={afterPostDelete}
      feedAccessor={feedAccessor}
      index={index}
      key={post?.uuid || index}
    />
  ))

  const renderNoPostsDisplay = () => (
    <NoPostsDisplay>
      <PiNewspaperBold size={size(2.75)} />
      &nbsp; No Posts Yet
    </NoPostsDisplay>
  )

  const renderSubscriberIcon = () => (
    <>
      &nbsp;
      <SubscriberIcon
        size={2.25}
        style={{ marginTop: size(-0.4) }}
      />
    </>
  )

  if (user?.uuid && !user?.isAnonymous && (userHasStats || hasPosts)) {
    return (
      <div>
        <SocialInfo>
          <SocialInfoLeft>
            <ProfileNavigateAvatar
              toNearby={false}
              src={thumbnailMediumBlue(user?.socialMedia?.thumbnail, user?.defaultAvatar)}
              uuid={user?.uuid}
              style={profilePictureStyles(isAvatar)}
            />
            <SocialInfoDisplayNames>
              <DisplayName>
                {user?.displayName}
                {user?.isSubscribed && renderSubscriberIcon()}
              </DisplayName>
              <Handle>@{user?.handle}</Handle>
            </SocialInfoDisplayNames>
          </SocialInfoLeft>
        </SocialInfo>
        {hasPosts ? <RecentPosts>Recent Posts</RecentPosts> : ''}
        {hasPosts
          ? renderPosts()
          : renderNoPostsDisplay()
        }
      </div>
    )
  } else {
    return undefined
  }
}

export default ProfileRecentPosts
